import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Search } from 'lucide-react';
import { Kbd, cn } from '@helios/ui';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import { useGlobalSearch } from '@/features/search/search.hooks';
import { useUiStore } from '@/stores/uiStore';
import { isMac } from '@/lib/platform';

export function HeaderSearch() {
  const navigate = useNavigate();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const setCommandPaletteOpen = useUiStore((state) => state.setCommandPaletteOpen);
  const permissions = useAuth().permissions;
  const canSearch = hasPermission(permissions, PERMISSIONS.SEARCH_READ);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const searchQuery = useGlobalSearch(debouncedQuery, canSearch && open);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => window.clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, []);

  const hits = searchQuery.data?.hits ?? [];
  const recent = searchQuery.data?.recentQueries ?? [];
  const showPanel =
    open && (query.trim().length >= 2 || searchQuery.isFetching || searchQuery.isError);
  const activeHit = hits[activeIndex];

  const goTo = (href: string) => {
    setOpen(false);
    setQuery('');
    navigate(href);
  };

  return (
    <div ref={rootRef} className="relative min-w-0 flex-1 md:max-w-md">
      <label className="sr-only" htmlFor="header-platform-search">
        Search the platform
      </label>
      <div
        className={cn(
          'backdrop-blur-glass flex h-7 items-center gap-2 rounded-full border border-white/10 bg-white/[0.045] px-2 py-0 text-xs leading-4 transition-[border-color,box-shadow,background-color] duration-200',
          'focus-within:border-ring focus-within:bg-white/[0.07] focus-within:shadow-[0_0_0_3px_hsl(var(--ring)/0.16),0_0_28px_-6px_hsl(var(--ring)/0.5)] hover:border-white/20',
        )}
      >
        <Search className="text-primary/85 h-4 w-4 shrink-0" aria-hidden="true" />
        <input
          id="header-platform-search"
          ref={inputRef}
          type="search"
          autoComplete="off"
          spellCheck={false}
          disabled={!canSearch}
          value={query}
          placeholder={
            canSearch
              ? 'Search projects, documents, people, finance…'
              : 'Search requires search:global:read'
          }
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={showPanel}
          aria-activedescendant={
            activeHit ? `${listId}-${activeHit.kind}-${activeHit.id}` : undefined
          }
          className="text-foreground placeholder:text-muted-foreground/70 h-full min-w-0 flex-1 bg-transparent focus:outline-none disabled:cursor-not-allowed"
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              inputRef.current?.blur();
              return;
            }
            if (event.key === 'ArrowDown' && hits.length > 0) {
              event.preventDefault();
              setActiveIndex((index) => (index + 1) % hits.length);
              return;
            }
            if (event.key === 'ArrowUp' && hits.length > 0) {
              event.preventDefault();
              setActiveIndex((index) => (index - 1 + hits.length) % hits.length);
              return;
            }
            if (event.key === 'Enter' && activeHit) {
              event.preventDefault();
              goTo(activeHit.href);
            }
          }}
        />
        <button
          type="button"
          className="ml-auto hidden items-center gap-0.5 sm:flex"
          aria-label="Open command palette"
          aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}
          onClick={() => setCommandPaletteOpen(true)}
        >
          <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>
          <Kbd>K</Kbd>
        </button>
      </div>

      {showPanel ? (
        <div
          id={listId}
          role="listbox"
          aria-label="Platform search results"
          className="scheme-dark bg-popover text-popover-foreground glass-strong absolute z-50 mt-1.5 w-full overflow-hidden rounded-xl"
        >
          {searchQuery.isError ? (
            <p className="text-destructive px-3 py-3 text-sm">
              {searchQuery.error instanceof Error
                ? searchQuery.error.message
                : 'Elasticsearch is unavailable.'}
            </p>
          ) : null}

          {searchQuery.isFetching && hits.length === 0 && !searchQuery.isError ? (
            <p className="text-muted-foreground px-3 py-3 text-sm">Searching records…</p>
          ) : null}

          {!searchQuery.isFetching &&
          !searchQuery.isError &&
          query.trim().length >= 2 &&
          hits.length === 0 ? (
            <p className="text-muted-foreground px-3 py-3 text-sm">
              No records match “{query.trim()}”.
            </p>
          ) : null}

          {searchQuery.data?.limitations?.map((limitation) => (
            <p key={limitation} role="status" className="text-muted-foreground px-3 py-2 text-xs">
              {limitation}
            </p>
          ))}

          {hits.length > 0 ? (
            <ul className="max-h-80 overflow-auto py-1">
              {hits.map((hit, index) => (
                <li key={`${hit.kind}-${hit.id}`}>
                  <button
                    type="button"
                    id={`${listId}-${hit.kind}-${hit.id}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                      index === activeIndex
                        ? 'bg-primary/15 text-foreground'
                        : 'hover:bg-primary/15',
                    )}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => goTo(hit.href)}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {hit.title}
                      <span className="text-muted-foreground ml-2 text-xs">{hit.subtitle}</span>
                    </span>
                    <span className="text-2xs text-muted-foreground shrink-0">{hit.kind}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {recent.length > 0 && hits.length === 0 && !searchQuery.isError ? (
            <p className="border-border text-muted-foreground border-t px-3 py-2 text-xs">
              Recent: {recent.map((item) => item.query).join(', ')}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
